import React, { useState, useRef } from 'react';
import { X, Upload, FileText, Check, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_SIZE_MB = 25;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const DOCUMENT_TYPES = [
  { value: 'veterinary_health_certificate', label: 'Veterinary Health Certificate' },
  { value: 'vaccination_records', label: 'Vaccination Records' },
  { value: 'microchip_documentation', label: 'Microchip Documentation' },
  { value: 'pet_passport', label: 'Pet Passport' },
  { value: 'import_permit', label: 'Import Permit' },
  { value: 'rabies_titer_test', label: 'Rabies Titer Test' },
  { value: 'pet_photos', label: 'Pet Photos' },
  { value: 'owner_passport', label: 'Owner Passport' },
  { value: 'other', label: 'Other' }
];

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const UploadDocumentModal = ({ relocation, clientId, onClose, onSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState('');
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const xhrRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    setErrorMessage('');
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setErrorMessage(
        `This file is ${formatBytes(file.size)} — the maximum size is ${MAX_FILE_SIZE_MB} MB. ` +
        `Try compressing the PDF, or upload pages one at a time.`
      );
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !documentType) {
      setErrorMessage('Please select a document type and a file.');
      return;
    }

    setUploading(true);
    setErrorMessage('');
    setProgress(0);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('document_type', documentType);
    formData.append('client_id', clientId);

    // Use XHR (not fetch) to get real upload progress events + abort support
    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;

    xhr.upload.addEventListener('progress', (evt) => {
      if (evt.lengthComputable) {
        setProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      setUploading(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        setProgress(100);
        toast.success('Document uploaded successfully!');
        onSuccess();
        onClose();
      } else {
        let detail = `Server returned ${xhr.status}. Please try again.`;
        try {
          const data = JSON.parse(xhr.responseText);
          if (data.detail) detail = data.detail;
        } catch (_) { /* non-JSON response */ }
        setErrorMessage(detail);
        logger.error('Upload failed:', xhr.status, xhr.responseText);
      }
    });

    xhr.addEventListener('error', () => {
      setUploading(false);
      setErrorMessage(
        'Upload failed — your connection dropped. This can happen on mobile networks. ' +
        'Please reconnect to Wi-Fi and try again.'
      );
      logger.error('Upload network error');
    });

    xhr.addEventListener('abort', () => {
      setUploading(false);
      setErrorMessage('Upload cancelled.');
    });

    xhr.addEventListener('timeout', () => {
      setUploading(false);
      setErrorMessage(
        'Upload timed out. Try a smaller file, a better connection, or contact support.'
      );
    });

    xhr.open('POST', `${API_URL}/api/client/relocations/${relocation.id}/upload`);
    xhr.timeout = 5 * 60 * 1000; // 5 minutes
    xhr.send(formData);
  };

  const handleCancel = () => {
    if (uploading && xhrRef.current) {
      xhrRef.current.abort();
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="upload-document-modal">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Upload Document</h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="upload-modal-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Relocation Info */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <p className="text-sm text-blue-900">
              <strong>Pet:</strong> {relocation.pet_name}
            </p>
            <p className="text-sm text-blue-900">
              <strong>Route:</strong> {relocation.origin} → {relocation.destination}
            </p>
          </div>

          {/* Document Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Document Type *
            </label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              data-testid="document-type-select"
              disabled={uploading}
            >
              <option value="">Select document type...</option>
              {DOCUMENT_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File *
              <span className="font-normal text-gray-500"> (max {MAX_FILE_SIZE_MB} MB)</span>
            </label>
            <div className="relative">
              <input
                type="file"
                onChange={handleFileSelect}
                accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,.doc,.docx,image/*,application/pdf"
                className="hidden"
                id="file-upload"
                data-testid="file-upload-input"
                disabled={uploading}
              />
              <label
                htmlFor="file-upload"
                className={`flex items-center justify-center w-full px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  uploading
                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                    : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                <div className="text-center">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    {selectedFile ? (
                      <span className="flex items-center gap-2 text-blue-600">
                        <FileText className="w-4 h-4" />
                        {selectedFile.name} ({formatBytes(selectedFile.size)})
                      </span>
                    ) : (
                      'Tap to select file (PDF, JPG, PNG, HEIC, DOC)'
                    )}
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Error message — persistent, not a toast */}
          {errorMessage && (
            <div
              className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800"
              data-testid="upload-error-message"
            >
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>{errorMessage}</p>
            </div>
          )}

          {/* Upload progress bar */}
          {uploading && (
            <div data-testid="upload-progress">
              <div className="flex justify-between items-center mb-1 text-sm">
                <span className="text-gray-700 font-medium">Uploading…</span>
                <span className="text-blue-600 font-semibold">{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-200"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Please keep this page open until the upload completes.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleCancel}
              variant="outline"
              className="flex-1"
              data-testid="upload-cancel-btn"
            >
              {uploading ? 'Cancel Upload' : 'Cancel'}
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || !documentType || uploading}
              className="flex-1 flex items-center justify-center gap-2"
              data-testid="upload-submit-btn"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Uploading…
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Upload
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
